/**
 * Household + household-membership types.
 *
 * Ported from legacy:
 *   - `frontend/src/services/householdService.ts` (`Household`, `HouseholdUpdate`,
 *     `HouseholdUpdateResult`, `DiscoveredStore`)
 *   - `frontend/src/types.ts` (`HouseholdSummary`, `HouseholdUser`, `HouseholdInvite`)
 *
 * Used by T7 (web shell) for auth-guarded routing (household membership check)
 * and by the Settings > Users / Stores screens that T8 will port.
 *
 * Schemas mirror what the unchanged backend returns today from
 * `backend/src/household/household.ts` + `.../households.ts`. Keep fields nullable
 * / optional where the backend is flexible — Zod will reject unexpected
 * *types*, but tolerate missing columns on older rows.
 */

import { z } from 'zod';
import { householdRoleSchema, pgNumeric, type HouseholdRole } from './primitives';

// Re-export primitive household types from this module so callers that
// import from '@shopista/shared/types' keep one discovery surface.
export { householdRoleSchema };
export type { HouseholdRole };

// ---------------------------------------------------------------------------
// Household (settings / current household)
// ---------------------------------------------------------------------------

export const householdSchema = z.object({
  id: z.string(),
  name: z.string(),
  invite_code: z.string(),
  store_discovery_available: z.boolean().optional(),
  latitude: pgNumeric.nullable().optional(),
  longitude: pgNumeric.nullable().optional(),
  address: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state_province: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  postal_code: z.string().nullable().optional(),
  discovery_radius_km: pgNumeric,
  // T12.2 — household context fields. All `.optional()` so older rows (and
  // any response that predates this field) still validate; nullable because
  // the backend leaves them NULL until the household fills them in. The
  // controlled-vocab strings (eating_style / cooking_frequency) stay plain
  // `z.string()` here — the backend is authoritative on the allowed set; the
  // client only carries the value back and forth unchanged.
  adults: z.number().nullable().optional(),
  kids: z.number().nullable().optional(),
  eating_style: z.string().nullable().optional(),
  cooking_frequency: z.string().nullable().optional(),
  travel_flag: z.boolean().nullable().optional(),
  diet: z.array(z.string()).optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Household = z.infer<typeof householdSchema>;

// T12.2 — household context controlled vocabularies. Exported as readonly
// value lists (not Zod schemas) so UI pickers and any client-side validation
// reference one source without re-listing the values; the backend + Postgres
// CHECK remain authoritative.
export const EATING_STYLES = ['scratch', 'both', 'eats-out', 'ready-to-eat'] as const;
export type EatingStyle = (typeof EATING_STYLES)[number];

export const COOKING_FREQUENCIES = ['rarely', 'sometimes', 'often', 'always'] as const;
export type CookingFrequency = (typeof COOKING_FREQUENCIES)[number];

export const AGE_GROUPS = ['child', 'teen', 'adult', 'senior'] as const;
export type AgeGroup = (typeof AGE_GROUPS)[number];

/**
 * Response shape from `GET /api/household`. Wraps Household in `household`.
 */
export const householdResponseSchema = z.object({
  household: householdSchema,
});
export type HouseholdResponse = z.infer<typeof householdResponseSchema>;

// Request body for `PATCH /api/household`.
//
// BUG13 D8 — the `discoverStores` flag is gone. Auto-discover fires
// server-side on a coord-resolving save when the household has zero stores
// selected, and the response carries `discoveredStores` for the client to
// surface as a checklist. The client never asks for discovery via this
// endpoint — that path was the auto-import side effect this Bug removed.
export const householdUpdateRequestSchema = z.object({
  name: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  stateProvince: z.string().optional(),
  country: z.string().optional(),
  postalCode: z.string().optional(),
  committedField: z.enum(['address', 'postalCode', 'both']).optional(),
  discoveryRadiusKm: z.number().optional(),
  // T12.2 — household context fields. Nullable: `null` clears, omit leaves
  // unchanged. Controlled-vocab strings stay plain `z.string()` (backend
  // validates the allowed set).
  adults: z.number().nullable().optional(),
  kids: z.number().nullable().optional(),
  eatingStyle: z.string().nullable().optional(),
  cookingFrequency: z.string().nullable().optional(),
  travelFlag: z.boolean().nullable().optional(),
  diet: z.array(z.string()).optional(),
});
export type HouseholdUpdateRequest = z.infer<typeof householdUpdateRequestSchema>;

// T12.2 — per-member age group / dietary exclusions.
// `PATCH /api/household/users/:id`.
export const updateMemberFieldsRequestSchema = z.object({
  ageGroup: z.string().nullable().optional(),
  exclusions: z.array(z.string()).optional(),
});
export type UpdateMemberFieldsRequest = z.infer<typeof updateMemberFieldsRequestSchema>;

// T12.2 — household-wide brand like/avoid. A brand is one of the two values,
// never both.
export const householdBrandPreferenceValueSchema = z.union([
  z.literal('like'),
  z.literal('avoid'),
]);
export type HouseholdBrandPreferenceValue = z.infer<typeof householdBrandPreferenceValueSchema>;

export const householdBrandPreferenceSchema = z.object({
  brand_id: z.string(),
  preference: householdBrandPreferenceValueSchema,
  displayorder: z.number(),
});
export type HouseholdBrandPreference = z.infer<typeof householdBrandPreferenceSchema>;

export const householdBrandPreferencesResponseSchema = z.object({
  preferences: z.array(householdBrandPreferenceSchema),
});
export type HouseholdBrandPreferencesResponse = z.infer<
  typeof householdBrandPreferencesResponseSchema
>;

export const linkHouseholdBrandPreferenceRequestSchema = z.object({
  brandId: z.string(),
  preference: householdBrandPreferenceValueSchema,
});
export type LinkHouseholdBrandPreferenceRequest = z.infer<
  typeof linkHouseholdBrandPreferenceRequestSchema
>;

// T12.2 (step 8) — household-wide system-product like/avoid. The exact mirror of
// the household-brand shape one level up. Unlike the brand rows (which carry
// only `brand_id`, mapped to a name client-side from the small brand list), the
// system-product rows embed the SystemProduct `name`/`image_url`: the catalog is
// too large to fetch wholesale, so the linked rows must carry their display name.
export const householdSystemProductPreferenceValueSchema = z.union([
  z.literal('like'),
  z.literal('avoid'),
]);
export type HouseholdSystemProductPreferenceValue = z.infer<
  typeof householdSystemProductPreferenceValueSchema
>;

export const householdSystemProductPreferenceSchema = z.object({
  systemproduct_id: z.string(),
  name: z.string(),
  image_url: z.string().nullable().optional(),
  preference: householdSystemProductPreferenceValueSchema,
  displayorder: z.number(),
});
export type HouseholdSystemProductPreference = z.infer<
  typeof householdSystemProductPreferenceSchema
>;

export const householdSystemProductPreferencesResponseSchema = z.object({
  preferences: z.array(householdSystemProductPreferenceSchema),
});
export type HouseholdSystemProductPreferencesResponse = z.infer<
  typeof householdSystemProductPreferencesResponseSchema
>;

export const linkHouseholdSystemProductPreferenceRequestSchema = z.object({
  systemProductId: z.string(),
  preference: householdSystemProductPreferenceValueSchema,
});
export type LinkHouseholdSystemProductPreferenceRequest = z.infer<
  typeof linkHouseholdSystemProductPreferenceRequestSchema
>;

// T12.5.1 (C) — household catalog preference signals (read-only). The
// per-product (catalog) brand + product preferences (preferred / substitute /
// exclude, CARRYING the value), rolled up to the household. The Deals/Planning
// surfaces render the full stacked badge set from these. Brands carry
// `brand_id` (mapped to a name client-side via the brand catalog); products
// embed `name` so the title/line whole-word match runs without a wholesale
// catalog fetch. Product entries are deliberate-only (`provenance='user'`,
// enforced backend-side).
export const catalogPreferenceValueSchema = z.union([
  z.literal('preferred'),
  z.literal('substitute'),
  z.literal('exclude'),
]);
export type CatalogPreferenceValue = z.infer<typeof catalogPreferenceValueSchema>;

export const catalogBrandSignalSchema = z.object({
  brand_id: z.string(),
  preference: catalogPreferenceValueSchema,
});
export type CatalogBrandSignal = z.infer<typeof catalogBrandSignalSchema>;

export const catalogProductSignalSchema = z.object({
  systemproduct_id: z.string(),
  name: z.string(),
  preference: catalogPreferenceValueSchema,
});
export type CatalogProductSignal = z.infer<typeof catalogProductSignalSchema>;

export const householdCatalogPreferenceSignalsResponseSchema = z.object({
  brands: z.array(catalogBrandSignalSchema),
  products: z.array(catalogProductSignalSchema),
});
export type HouseholdCatalogPreferenceSignalsResponse = z.infer<
  typeof householdCatalogPreferenceSignalsResponseSchema
>;

// T12.2 — per-store category affinity. `PUT /selected-stores/:storeId/categories`.
export const setStoreCategoriesRequestSchema = z.object({
  categories: z.array(z.string()),
});
export type SetStoreCategoriesRequest = z.infer<typeof setStoreCategoriesRequestSchema>;

// BUG57 — an online-store candidate: a retailer's synthetic online
// `storelocations` row whose pricing zone covers the household's postal-code
// region. Deliberately its OWN schema, not folded into `discoveredStoreSchema`:
// that schema REQUIRES `latitude`, `longitude`, and `distanceKm`, which an
// online candidate can never supply (online rows carry no coordinates by
// design), and relaxing it to accommodate them would weaken the "physical lane
// unchanged" guarantee those required fields exist to protect. Keyed on
// `storeLocationId` ONLY — online rows carry no `place_id`, and the physical
// candidate mapper's `placeId ?? ''` fallback would collide every online
// candidate on the same empty string if this schema echoed a `placeId` field.
export const onlineStoreCandidateSchema = z.object({
  storeLocationId: z.string(),
  name: z.string(),
});
export type OnlineStoreCandidate = z.infer<typeof onlineStoreCandidateSchema>;

/**
 * Response shape from `PATCH /api/household`.
 *
 * BUG13 — when the save resolved coords AND the household had zero stores
 * selected beforehand, the response carries `discoveredStores`: a candidate
 * list the UI renders as a checklist. Each entry carries `suggestedSelected`
 * so the major chains (Walmart, Costco, No Frills, Superstore) pre-check.
 * No `storelocations` rows are written by this endpoint — the user must
 * click Save in the Stores card to persist their selection.
 *
 * `geocodeError` carries a short code (`ZERO_RESULTS`, `NETWORK_ERROR`,
 * `INVALID_RESPONSE`, etc.) when the backend tried to geocode an address but
 * failed. The address is still persisted on geocode failure — losing
 * geocoding must not break the save.
 *
 * BUG57 — `onlineStores` carries the online-store lane: it is NOT gated on
 * the same "coords resolved + zero prior selections" condition as
 * `discoveredStores` above — it is computed from the household's PERSISTED
 * postal code on every PATCH, regardless of whether this save resolved
 * coordinates. Declared here (not folded into `discoveredStores`) — see
 * `onlineStoreCandidateSchema`'s own comment for why. Zod strips undeclared
 * keys, so this field MUST be declared here (mirroring the existing
 * `storeLocationId` precedent comment above) or the backend's `onlineStores`
 * payload is silently dropped before the client ever sees it.
 */
export const householdUpdateResponseSchema = z.object({
  household: householdSchema,
  discoveredStores: z
    .array(
      z.object({
        placeId: z.string(),
        name: z.string(),
        address: z.string(),
        latitude: pgNumeric,
        longitude: pgNumeric,
        distanceKm: pgNumeric,
        suggestedSelected: z.boolean(),
        // Physical postal code per candidate. Optional; a given catalog row may
        // carry none.
        postalCode: z.string().optional(),
        // Store website per candidate — the store→retailer registrable-domain
        // signal. Optional; must be retained here or Zod strips it before the
        // client echoes the candidate back to `/import-stores`.
        website: z.string().optional(),
        // The catalog row id, carried through so the resolve-only selection path
        // keys on it. Optional (a legacy placeId-only echo carries none); retained
        // here so it survives the auto-discover-on-first-save response parse.
        storeLocationId: z.string().optional(),
      })
    )
    .optional(),
  onlineStores: z.array(onlineStoreCandidateSchema).optional(),
  geocodeError: z.string().optional(),
});
export type HouseholdUpdateResponse = z.infer<typeof householdUpdateResponseSchema>;

// ---------------------------------------------------------------------------
// Store discovery / search / import
// ---------------------------------------------------------------------------

// A discovered store is a SELECTION CANDIDATE resolved against the shared
// `storelocations` catalog — NOT a Google import. Since T26.3 the household
// discover/select path is DB-sourced and Google-free: `discoverStoresForHousehold`
// reads active physical catalog rows, and every candidate carries its existing
// `storeLocationId` (see backend `mergeDbStoresIntoCandidates`). Google survives
// only on the admin catalog-build path (`discoverNearbyStores` / `fetchPlaceDetails`).
//
// `suggestedSelected` — true when the normalized store name matches the backend
// major-chains allowlist (Walmart, Costco, No Frills, Superstore); the Stores-card
// checklist uses it to pre-check the suggested rows.
//
// `postalCode` / `website` — physical fields carried on the response so the
// FlyerDeals resolver can derive an FSA and the store→retailer matcher gets the
// registrable-domain signal. Optional because they may be absent on a given row.
//
// `storeLocationId` — the catalog row's id. Carried on the DISCOVERY RESPONSE so
// the DB-sourced id survives the client-side parse (Zod's default `.strip()`
// would otherwise drop it, since the field wasn't declared before), and echoed
// back on the IMPORT REQUEST where the resolve-only selection path keys on it.
// Optional here because a legacy placeId-only echo carries none.
export const discoveredStoreSchema = z.object({
  placeId: z.string(),
  name: z.string(),
  address: z.string(),
  latitude: pgNumeric,
  longitude: pgNumeric,
  distanceKm: pgNumeric,
  suggestedSelected: z.boolean(),
  postalCode: z.string().optional(),
  website: z.string().optional(),
  // The catalog row id. Optional on the response so a legacy placeId-only
  // candidate still validates, but the DB-serve populates it on every real
  // candidate — declaring it here is what stops Zod stripping it before the
  // client can echo it back to the import request.
  storeLocationId: z.string().optional(),
});
export type DiscoveredStore = z.infer<typeof discoveredStoreSchema>;

// BUG57 — `onlineStores` mirrors the same field on `householdUpdateResponseSchema`
// (see that field's comment for the full rationale: own schema, own key,
// `storeLocationId`-only). Declared here too — Zod strips undeclared keys, and
// this is the OTHER entry point (`POST /discover-stores`) that must carry it.
export const discoveredStoresResponseSchema = z.object({
  stores: z.array(discoveredStoreSchema),
  onlineStores: z.array(onlineStoreCandidateSchema).optional(),
});
export type DiscoveredStoresResponse = z.infer<typeof discoveredStoresResponseSchema>;

// The import (selection) candidate — distinct from the strict discovery-RESPONSE
// schema above. The physical fields (`placeId`, coords, address) are now all
// optional because an id-only curated pick carries none of them; a candidate is
// resolved by `resolveStoreLocationId`'s order (`storeLocationId` → `placeId` →
// none), so `storeLocationId` is AUTHORITATIVE when present.
//
// The `.refine` requires AT LEAST ONE resolver key — a non-empty `storeLocationId`
// OR a non-empty `placeId`. This deliberately is NOT an exactly-one rule: the
// normal DB-serve candidate carries BOTH (see backend `mergeDbStoresIntoCandidates`,
// which sets `storeLocationId` and passes `placeId` through from the row), and an
// exactly-one refine would reject that happy path. Accepts three shapes: the
// both-keys DB candidate, the id-only curated pick, and a legacy placeId-only echo.
export const importStoreCandidateSchema = z
  .object({
    storeLocationId: z.string().optional(),
    placeId: z.string().optional(),
    name: z.string().optional(),
    address: z.string().optional(),
    latitude: pgNumeric.optional(),
    longitude: pgNumeric.optional(),
    distanceKm: pgNumeric.optional(),
    suggestedSelected: z.boolean().optional(),
    postalCode: z.string().optional(),
    website: z.string().optional(),
  })
  .refine(
    (c) =>
      (typeof c.storeLocationId === 'string' && c.storeLocationId.length > 0) ||
      (typeof c.placeId === 'string' && c.placeId.length > 0),
    {
      message: 'each store must carry a storeLocationId or a non-empty placeId',
    },
  );
export type ImportStoreCandidate = z.infer<typeof importStoreCandidateSchema>;

export const importStoresRequestSchema = z.object({
  stores: z.array(importStoreCandidateSchema),
});
export type ImportStoresRequest = z.infer<typeof importStoresRequestSchema>;

export const importStoresResponseSchema = z.object({
  imported: z.number(),
  storeIds: z.array(z.string()),
});
export type ImportStoresResponse = z.infer<typeof importStoresResponseSchema>;

export const selectedStoresResponseSchema = z.object({
  storeIds: z.array(z.string()).nullable().optional(),
});
export type SelectedStoresResponse = z.infer<typeof selectedStoresResponseSchema>;

// T12.2 — one entry in `households.store_preferences.selected[]`, mirroring the
// backend `StorePreferenceEntry` shape. `categories` is the declared per-store
// category affinity (optional; soft signal for T12.5).
export const storePreferenceEntrySchema = z.object({
  storelocation_id: z.string(),
  is_favorite: z.boolean(),
  display_order: z.number(),
  distance_km: z.number(),
  categories: z.array(z.string()).optional(),
});
export type StorePreferenceEntry = z.infer<typeof storePreferenceEntrySchema>;

// Response shape from `GET /api/household/store-preferences` — the full
// selected-store preference entries (ids + favourite + order + categories).
export const storePreferencesResponseSchema = z.object({
  selected: z.array(storePreferenceEntrySchema),
});
export type StorePreferencesResponse = z.infer<typeof storePreferencesResponseSchema>;

// ---------------------------------------------------------------------------
// Household memberships (multi-household user)
// ---------------------------------------------------------------------------

export const membershipStatusSchema = z.enum(['active', 'left']);
export type MembershipStatus = z.infer<typeof membershipStatusSchema>;

export const householdSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  invite_code: z.string(),
  role: householdRoleSchema,
  status: membershipStatusSchema,
  joined_at: z.string(),
  left_at: z.string().nullable().optional(),
  // The server computes this as `household.id = user.default_household_id`
  // (households.ts route). When the user has no default household set (a
  // freshly created/seeded account, before a default is chosen) the SQL
  // comparison yields NULL, so the wire value is `boolean | null`. Nullable
  // here to match the server — a non-nullable schema rejected the whole
  // households list for such a user, which silently broke every reader of
  // `useHouseholds()` (role detection, the first-run gate). Readers treat
  // null as "not the current household" (`is_current === true` is the only
  // truthy branch).
  is_current: z.boolean().nullable(),
});
export type HouseholdSummary = z.infer<typeof householdSummarySchema>;

export const householdsResponseSchema = z.object({
  households: z.array(householdSummarySchema),
});
export type HouseholdsResponse = z.infer<typeof householdsResponseSchema>;

// ---------------------------------------------------------------------------
// Household users / invites
// ---------------------------------------------------------------------------

export const householdUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  display_name: z.string().nullable().optional(),
  last_seen_at: z.string().nullable().optional(),
  role: householdRoleSchema,
  status: membershipStatusSchema,
  joined_at: z.string(),
  // T12.2 — per-member fields (age group / dietary exclusions).
  // `.optional()` so any response that predates the read-shape addition still
  // validates; `age_group` is nullable until the member sets it. The
  // controlled-vocab string stays plain `z.string()` — the backend + Postgres
  // CHECK are authoritative on the allowed set.
  age_group: z.string().nullable().optional(),
  exclusions: z.array(z.string()).optional(),
});
export type HouseholdUser = z.infer<typeof householdUserSchema>;

export const householdUsersResponseSchema = z.object({
  users: z.array(householdUserSchema),
});
export type HouseholdUsersResponse = z.infer<typeof householdUsersResponseSchema>;

export const householdInviteSchema = z.object({
  id: z.string(),
  email: z.string(),
  role: householdRoleSchema,
  expires_at: z.string(),
  created_at: z.string(),
  accepted_at: z.string().nullable().optional(),
});
export type HouseholdInvite = z.infer<typeof householdInviteSchema>;

export const householdInvitesResponseSchema = z.object({
  invites: z.array(householdInviteSchema),
});
export type HouseholdInvitesResponse = z.infer<typeof householdInvitesResponseSchema>;

export const createInviteRequestSchema = z.object({
  email: z.string(),
  role: householdRoleSchema,
});
export type CreateInviteRequest = z.infer<typeof createInviteRequestSchema>;

export const createInviteResponseSchema = z.object({
  invite: householdInviteSchema,
  inviteUrl: z.string(),
  emailSent: z.boolean(),
});
export type CreateInviteResponse = z.infer<typeof createInviteResponseSchema>;

// ---------------------------------------------------------------------------
// Multi-household membership mutations (join / switch / leave / delete)
// ---------------------------------------------------------------------------

// Reuse `userSchema` from `./user` for nested user payloads. Imported below.
import { userSchema } from './user';

/**
 * Join/switch/leave/delete responses share a common `{ success, user?, sessionToken?, loggedOut? }`
 * envelope. Backend sometimes returns only `{ success: true }` (leave another
 * household while staying in current), so `user` + `sessionToken` are optional.
 */
export const householdMembershipMutationResponseSchema = z.object({
  success: z.boolean(),
  user: userSchema.optional(),
  sessionToken: z.string().optional(),
  loggedOut: z.boolean().optional(),
});
export type HouseholdMembershipMutationResponse = z.infer<
  typeof householdMembershipMutationResponseSchema
>;

export const joinHouseholdRequestSchema = z
  .object({
    inviteCode: z.string().optional(),
    inviteToken: z.string().optional(),
  })
  .refine((data) => data.inviteCode || data.inviteToken, {
    message: 'inviteCode or inviteToken is required',
  });
export type JoinHouseholdRequest = z.infer<typeof joinHouseholdRequestSchema>;

export const switchHouseholdRequestSchema = z.object({
  householdId: z.string(),
});
export type SwitchHouseholdRequest = z.infer<typeof switchHouseholdRequestSchema>;
